import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardTitle, Col, Collapse, Container, Row } from 'reactstrap';
import { ContactId } from '@business/entities/contact';
import { LabourItemResponse } from '@business/entities/order';
import { OrgAddressResponse, OrgAddressType, OrgResponse } from '@business/entities/organization';
import { CommonService, OrderService, OrgService, ProposalService } from '@business/services';
import { Button } from '@core/components/Button';
import { FormCheckbox, FormDateInput, FormInput, FormSelect } from '@core/components/form';
import { FormUserAvatar } from '@core/components/form/FormUserAvatar';
import { LoadingIndicator } from '@core/components/LoadingIndicator';
import { useHistory, useQueryStringParam, useRouteParam } from '@core/router';
import { useBloc } from '@core/utils/bloc';
import { useFormConfig, useFormState, ValidationFn } from '@core/utils/form';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { PageHeader } from '@modules/common';
import { CBreadcrumbItem } from '@modules/common/Breadcrumb';
import { InfoBanner } from '@modules/common/InfoBanner';
import { InvoiceDiscount } from '@modules/common/InvoiceDiscount';
import { InvoiceSalesTax } from '@modules/common/InvoiceSalesTax';
import { openAddEditAddress } from '@modules/organizations/OrganizationPage/AddEditAddressModal';
import { openAddEditContactModal } from '@modules/people/AddEditContactModal';
import { BehaviorSubject, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { ShippingMethodControl } from '../ShippingMethodControl/ShippingMethodControl';
import { AddEditTimeMaterialPageBloc } from './AddEditTimeMaterialPageBloc';
import { defineOrderForm, OrderFormData } from './TimeMaterialForm';
import { TimeMaterialLineStep } from './TimeMaterialLineStep';
import { TimeMaterialPreview } from './TimeMaterialPreview';
import { EstimateLineItem, TMStageType, TimeMaterialResponse } from '@business/entities/tm';
import { openTMSectionModal } from '../ManageTMSectionsModal';
import { openAddProductOverlay } from '../AddProductOverlay';
import { SelectedProduct } from '../AddProductOverlay/AddProductOverlay';
import { ProposalDiscountFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { ProposalDiscount } from '@modules/common/ProposalDiscount';
import { TimeMaterialFeeLine } from './TimeMaterialFeeLine';
import { R } from '@core/utils/r';
import { openViewHTMLModal } from '@modules/common/ViewHTMLModal';
import './index.scss';

export const orderAddresses = new BehaviorSubject<OrgAddressResponse[]>([]);

export const validateOrderAddress: ValidationFn = control => {
  const value = control.value;
  const address = orderAddresses.value.find(a => a.id === value);

  if (!address) {
    return null;
  }

  if (address && OrgService.isValidAddress(address)) {
    return null;
  }

  if (R.isEmpty(address.city)) {
    return {
      type: 'address',
      message: 'City is missing.',
    };
  }
  if (R.isEmpty(address.state)) {
    return {
      type: 'address',
      message: 'State is missing.',
    };
  }
  if (R.isEmpty(address.postal)) {
    return {
      type: 'address',
      message: 'Postal code is missing.',
    };
  }
  if (R.isEmpty(address.country)) {
    return {
      type: 'address',
      message: 'Country is missing.',
    };
  }

  return null;
};

export const AddEditTimeMaterialPage = memo(() => {
  const orgId = useRouteParam('orgId');
  const orderId = useRouteParam('id', true);
  const boardId = useQueryStringParam('boardId') || '1';

  const settings = useSettings();
  const form = useMemo(() => defineOrderForm(settings?.invoiceClassRequired), [settings]);
  const bloc = useBloc(AddEditTimeMaterialPageBloc, orgId, boardId, orderId);
  const users = useObservable(bloc.users$, []);
  const products = useObservable(bloc.products$, []);
  const packages = useObservable(bloc.packages$, []);
  const services = useObservable(bloc.services$, []);
  const classes = useObservable(bloc.classes$, []);
  const terms = useObservable(bloc.terms$, []);
  const stages = useObservable(bloc.stages$, []);
  const tmFees = useObservable(bloc.tmFees$, []);
  const labourTypes = useObservable(bloc.labourTypes$, []);
  const tmBoards = useObservable(bloc.tmBoards$, []);
  const formValue = useNonNilObservable(form.value$);
  const organization = useObservable(bloc.organization$, null) as OrgResponse | null;
  const order = useObservable(bloc.order$, null) as TimeMaterialResponse | null;
  const vendors = useObservable(bloc.vendors$, []);
  const salesTax = useObservable(bloc.salesTax$, []);
  const addresses = useObservable(bloc.addresses$, []);
  const [showSendMenu, toggleSendMenu] = useState(false);
  const [isSetting, setSetting] = useState(false);
  const [contactId, setContactId] = useState<ContactId>('0');
  const lineItems = useObservable(form.controls.lineItems.items$, []);
  const history = useHistory();
  const defaultAddress = useMemo(() => OrgService.getDefaultAddress(organization), [organization]);
  const contacts = organization ? organization.contacts : [];
  const termItems = useMemo(() => CommonService.selectItems(terms, 'name', 'id'), [terms]);
  const estimateStage = useMemo(() => R.find(stages, s => s.type === TMStageType.estimate), [stages]);
  const contactItems = useMemo(() => CommonService.selectItems(contacts, c => CommonService.getFullName(c), 'id'), [contacts]);
  const addressItems = useMemo(() => CommonService.selectItems(addresses, a => `${OrgService.getAddressString(a)}`, 'id'), [addresses]);
  const tiers = useObservable(bloc.tiers$, []);
  const defaultTier = useMemo(() => tiers.find(t => !!t.isDefault), [tiers]);
  const defaultTierId = useMemo(() => defaultTier && defaultTier.id, [defaultTier]);

  const shippingOptionItems = useMemo(
    () => CommonService.selectItems(OrderService.allShippingOptions(), s => OrderService.getShippingOptionTitle(s)),
    [],
  );
  const sAddressItems = useMemo(
    () =>
      CommonService.selectItems(
        R.filter(addresses, a => a.type === OrgAddressType.shipping || a.type === OrgAddressType.both),
        a => OrgService.getAddressString(a),
        'id',
      ),
    [addresses],
  );
  const isEdit = useMemo(() => !!order, [order]);
  const tmBoardItems = useMemo(() => CommonService.selectItems(tmBoards, 'name', 'id'), [tmBoards]);
  const isSalesTaxEnabled = useMemo(() => {
    if (settings) {
      return settings.tm.isSalesTax;
    }
    return false;
  }, [settings]);

  useEffect(() => {
    if (tmFees.length > 0) {
      if (order) {
        const currentFees = order?.fees || [];
        form.controls.fees.setValue(
          tmFees.map(o => {
            const initialFee = currentFees.find(f => f.id == o.id);
            return {
              id: o.id,
              name: o.name,
              service: o.projectService.title,
              serviceId: o.projectService.id,
              percent: (initialFee && initialFee.percent) ?? o.percent,
              isChecked: initialFee ? initialFee.isChecked == true || (initialFee.isChecked as any) == 'true' : false,
              feeTotal: (initialFee && initialFee.feeTotal) ?? 0,
              taxable: (initialFee && initialFee.taxable) ?? false,
              feeType: initialFee && initialFee.feeType ? initialFee.feeType : o.feeType,
            };
          }),
        );
      } else {
        form.controls.fees.setValue(
          tmFees.map(o => {
            return {
              id: o.id,
              name: o.name,
              service: o.projectService.title,
              serviceId: o.projectService.id,
              percent: o.percent,
              isChecked: false,
              feeTotal: 0,
              taxable: o.isTaxable,
              feeType: o.feeType,
            };
          }),
        );
      }
    }
  }, [form.controls.fees, order, tmFees]);

  useEffect(() => {
    form.patchValue({
      orgId: orgId,
      boardId: boardId,
    });
  }, [boardId, form, orgId]);

  useEffect(() => {
    if (formValue.boardId) {
      bloc.fetchStages(formValue.boardId).subscribe();
    }
  }, [bloc, formValue.boardId]);

  useEffect(() => {
    if (organization && R.toNumber(organization.termId) > 0) {
      form.patchValue({
        termId: organization.termId,
      });
    }
  }, [form, organization]);
  useEffect(() => {
    if (defaultAddress && !formValue!.billingAddress) {
      form.patchValue({ billingAddress: defaultAddress.id, shippingAddress: defaultAddress.id });
    }
    if (sAddressItems.length > 0 && !formValue!.shippingAddress) {
      form.patchValue({ shippingAddress: sAddressItems[0].value });
    }
  }, [form, defaultAddress, formValue, sAddressItems]);

  useEffect(() => {
    if (formValue && !formValue.contact && contacts.length) {
      form.patchValue({ contact: contacts[0].id });
    }
  }, [form, formValue, contacts]);

  useEffect(() => {
    if (formValue!.contact && formValue!.contact !== contactId && R.isEmpty(formValue?.billingEmail)) {
      const selectedContact = R.find(contacts, c => c.id === formValue!.contact);
      const email = selectedContact ? R.first(selectedContact.emails) : undefined;
      form.patchValue({ billingEmail: email ? email.value : undefined });
      setContactId(formValue!.contact);
    }
  }, [contacts, form, formValue, contactId]);

  useEffect(() => {
    if (estimateStage?.active) {
      form.patchValue({
        type: 'estimate',
      });
    }
  }, [estimateStage, form]);

  const getLabourType = useCallback((item: EstimateLineItem) => {
    if (item.labourItems.length == 1) {
      return item.labourItems[0].labourTypeId;
    } else if (item.labourItems.length > 1) {
      return '0';
    } else {
      return undefined;
    }
  }, []);

  const getLineLaborPrice = useCallback((items: LabourItemResponse[]) => {
    return items.reduce((total, item) => total + item.hour * R.roundDecimal(item.labourType.price, 2), 0);
  }, []);

  const getLineLaborCost = useCallback((items: LabourItemResponse[]) => {
    return items.reduce((total, item) => total + R.roundDecimal(item.labourType.cost, 2), 0);
  }, []);

  useEffect(() => {
    if (order && order.estimate) {
      const lastSection = R.last(order.sections);
      const sectionId = lastSection ? lastSection.id : -1;
      form.patchValue({
        contact: order.contact.id,
        billingEmail: order.billingEmail,
        billingAddress: order.billingAddress.id,
        shippingAddress: order.shippingAddress.id,
        termId: order.term.id,
        memo: order.estimate.memo,
        message: order.estimate.message,
        salesReps: order.salesReps,
        shippingOption: order.shippingOption,
        customerPoNo: order.customerPoNo,
        boardId: order.boardId,
        name: order.name,
        sections: order.sections.map(s => ({
          id: s.id,
          title: s.title,
          menuOrder: s.menuOrder,
          checked: false,
        })),
        orderNumber: order.orderNumber,
        isHide: order.isHide,
        lineItems: order.estimate.items.map((item, index) => {
          const isService = !R.isEmpty(item.lineCopyId) && item.lineCopyId.indexOf('service-') > -1;
          const pId = item.packageItems.length > 0 ? 'package-' + item.id : R.toString(item.productId);
          const labourItems = item.labourItems.map(l => ({
            labourTypeId: l.labourTypeId,
            hour: l.hour,
            cost: l.cost,
            price: l.price,
            labourTypes: labourTypes,
          }));
          let isServiceLaborPhase = item.isServicePhase ?? false;

          if (R.toNumber(item.serviceId) > 0 && !isServiceLaborPhase) {
            const currentLabor = R.find(labourTypes, l => l.name == item.description);
            isServiceLaborPhase = currentLabor ? true : false;
          }

          return {
            isServicePhase: isServiceLaborPhase,
            isDiscount: item.isDiscount,
            productImage: item.productImage,
            product: pId.includes('package') ? pId : pId == '0' ? 'service-' + item.serviceId : 'product-' + pId,
            description: item.description,
            quantity: item.quantity ?? 0,
            rate: item.unitPrice ?? 0,
            cost: item.cost ?? 0,
            vendorId: item.vendorId,
            classId: item.classId,
            tierId: item.tierId,
            id: item.id,
            lineTitle: (item.packageItems.length > 0 ? item.title : item.productSku) as string,
            unitOfMeasure: item.unitOfMeasure,
            lineProduct: null,
            taxable: item.taxable,
            labour: item.labourItems.reduce((total, item) => total + R.roundDecimal(item.hour, 2), 0),
            labourType: getLabourType(item),
            labourCost: getLineLaborCost(item.labourItems),
            labourPrice: getLineLaborPrice(item.labourItems),
            labourItems: labourItems,
            productLabourItems: labourItems,
            sectionId: isService ? sectionId : item.sectionId,
            packageItems: item.packageItems.map(o => {
              const labourItemsChild = o.labourItems.map(z => ({
                labourTypeId: z.labourTypeId,
                hour: z.hour,
                cost: z.cost,
                price: z.price,
                labourTypes: labourTypes,
              }));
              return {
                isServicePhase: o.isServicePhase ?? false,
                isDiscount: o.isDiscount,
                productImage: o.productImage,
                product: o.productId == '0' ? 'service-' + o.serviceId : 'product-' + o.productId,
                description: o.description,
                quantity: o.quantity,
                rate: o.unitPrice,
                cost: o.cost,
                vendorId: o.vendorId,
                classId: o.classId,
                tierId: o.tierId,
                id: o.id,
                lineTitle: o.productSku,
                unitOfMeasure: o.unitOfMeasure,
                lineProduct: null,
                taxable: o.taxable,
                labour: o.labourItems.reduce((total, x) => total + R.roundDecimal(x.hour, 2), 0),
                labourType: getLabourType(o),
                labourCost: getLineLaborCost(o.labourItems),
                labourPrice: getLineLaborPrice(o.labourItems),
                labourItems: labourItemsChild,
                productLabourItems: labourItemsChild,
                sectionId: o.sectionId,
                packageItems: [],
              };
            }),
          };
        }),
        discount: order.estimate.discount as ProposalDiscountFormData,
        salesTax: order.estimate.salesTax,
      });
      const productIds = R.flatMap(order.estimate.items, item => [item.productId]);
      bloc.searchProducts('', productIds).subscribe();
      bloc.loadAddresses([order.shippingAddress.id, order.billingAddress.id]).subscribe();
    }
  }, [bloc, form, getLabourType, getLineLaborCost, getLineLaborPrice, labourTypes, order]);

  const subTotal = formValue.lineItems.reduce((total, line) => total + R.toNumber(ProposalService.getSubTotalAddProd(line, false)), 0);

  const onSubmit = useCallback(
    (data: OrderFormData) => {
      return of(null).pipe(
        switchMap(() => {
          return of({
            changeStatus: false,
          });
        }),
        switchMap(response => {
          const formData = {
            ...data,
            ...response,
            subTotal: subTotal,
            // boardId: formValue.boardId,
            labourItems: data.lineItems.map(l => {
              return {
                ...l,
                labourItems: l.labourItems.map(i => {
                  return {
                    labourTypeId: i.labourTypeId,
                    hour: i.hour,
                    cost: i.cost,
                    price: i.price,
                  };
                }),
              };
            }),
          };
          if (order) {
            const updatedLineItems = data.lineItems.filter(l => !l.isServicePhase);
            formData.lineItems = updatedLineItems;
            return bloc.updateEstimate(order.id, formData).pipe(tap(() => history.push(`/time-materials/detail/${order.id}`)));
          } else {
            return bloc.createOrder(formData).pipe(tap(data => history.push(`/time-materials/detail/${data.id}`)));
          }
        }),
      );
    },
    [bloc, history, order, subTotal],
  );

  useFormConfig(form, { onSubmit });

  const { submitting } = useFormState(form, { submitting: true });

  const breadcrumb: CBreadcrumbItem[] = useMemo(() => {
    return [{ title: 'Home', link: '/' }, { title: `${gcLabel('Estimate', settings)}` }];
  }, [settings]);

  const loadAddresses = useCallback(() => {
    bloc
      .loadAddresses([formValue?.shippingAddress || '0', formValue?.billingAddress || '0'])
      .pipe(tap(() => form.validate()))
      .subscribe();
  }, [bloc, form, formValue]);

  const onProductSelection = useCallback(
    (items: SelectedProduct[]) => {
      const isCanadaFirst = CommonService.isCanadaFirst();
      items.forEach(item => {
        const index = R.findIndex(
          form.controls.lineItems.items,
          l => l.controls.sectionId.value == item.sectionId && R.isEmpty(l.controls.product.value),
        );
        if (index > -1) {
          form.controls.lineItems.removeAt(index);
        }
        const ctrl = form.controls.lineItems.push();
        ctrl.patchValue({
          ...item.productCtrl,
          //   product:
          //     item.productCtrl.packageItems.length > 0 ? `package-${R.toNumber(item.product.id) || 0}` : `product-${R.toNumber(item.product.id) || 0}`,
          sectionId: item.sectionId,
          id: 0,
          taxable: isCanadaFirst ? true : item.productCtrl.taxable,
        });
      });
    },
    [form],
  );

  const allLineItemsValues = formValue.lineItems.map(l => {
    if (l.packageItems.length > 0) {
      return l.packageItems.map(p => {
        return {
          title: p.description,
          qty: p.quantity,
          rate: p.rate,
          taxable: p.taxable,
        };
      });
    } else {
      return {
        title: l.description,
        qty: l.quantity,
        rate: l.rate,
        taxable: l.taxable,
      };
    }
  });

  // flat the array
  const flatAllLineItemsValues = R.flatten(allLineItemsValues);

  const canadaFirstErrors = useMemo(() => {
    const isCanadaFirst = CommonService.isCanadaFirst();
    if (!isCanadaFirst) return [];

    const errors: string[] = [];
    formValue.lineItems.forEach((lineItem, index) => {
      if (lineItem.product && !lineItem.taxable) {
        const itemTitle = lineItem.description || lineItem.lineTitle || `Line Item #${index + 1}`;
        errors.push(`Sales tax must be enabled for "${itemTitle}" (Canada First account requirement)`);
      }

      lineItem.packageItems.forEach((packageItem, pIndex) => {
        if (packageItem.product && !packageItem.taxable) {
          const packageTitle = packageItem.description || packageItem.lineTitle || `Package Item #${pIndex + 1}`;
          errors.push(`Sales tax must be enabled for "${packageTitle}" (Canada First account requirement)`);
        }
      });
    });
    return errors;
  }, [formValue.lineItems]);

  return (
    <div className="page-content">
      <Container fluid>
        <PageHeader breadcrumbItems={breadcrumb}>{order?.orderNumber || 'Create Order'}</PageHeader>
        <Card>
          <CardBody>
            {!organization && <LoadingIndicator />}
            {OrderService.isNameEnabled() && (
              <Row>
                <Col lg={6}>
                  <FormInput control={form.controls.name} />
                </Col>
                <Col lg={6}>
                  <FormInput control={form.controls.orderNumber} />
                </Col>
              </Row>
            )}
            <Row>
              <Col lg={6}>
                <FormSelect
                  control={form.controls.contact}
                  onChange={cid => {
                    const contact = contacts.find(c => c.id === cid);
                    if (!contact) return;
                    if (!R.first(contact.emails)) return;
                    if (R.isEmpty(formValue.billingEmail)) {
                      form.patchValue({ billingEmail: R.first(contact.emails)!.value });
                    } else if (formValue.billingEmail !== R.first(contact.emails)!.value) {
                      if (confirm('Do you want to change the billing email?')) {
                        form.patchValue({ billingEmail: R.first(contact.emails)!.value });
                      }
                    }
                  }}
                  items={contactItems}
                />
                {organization && (
                  <a className="text-primary" onClick={() => openAddEditContactModal({ organization, reloadOrg: true })}>
                    Add {gcLabel('Contact')}
                  </a>
                )}
              </Col>
              <Col lg={6}>
                <FormInput withoutGroup control={form.controls.billingEmail} />
                <div className="mb-2 text-muted">
                  <small>Separate multiple email addresses using commas</small>
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg="6" style={{ marginBottom: '1rem' }}>
                <div className="form-group">
                  {!!form.controls.billingAddress.errors && (
                    <span className="text-right" style={{ float: 'right' }}>
                      <a
                        className="text-primary"
                        onClick={() =>
                          openAddEditAddress({
                            orgId,
                            address: R.find(addresses, a => a.id === formValue?.billingAddress),
                            onDone: () => loadAddresses(),
                          })
                        }
                      >
                        <i className="fa fa-edit"></i>
                      </a>
                    </span>
                  )}
                  <FormSelect control={form.controls.billingAddress} items={addressItems} />
                </div>

                <a className="text-primary" onClick={() => openAddEditAddress({ orgId, type: OrgAddressType.both, onDone: () => loadAddresses() })}>
                  Add Address
                </a>
              </Col>
              <Col lg="6" style={{ marginBottom: '1rem' }}>
                {!!form.controls.shippingAddress.errors && (
                  <span className="text-primary" style={{ float: 'right' }}>
                    <a
                      className="text-primary"
                      onClick={() =>
                        openAddEditAddress({
                          orgId,
                          address: R.find(addresses, a => a.id === formValue?.shippingAddress),
                          onDone: () => loadAddresses(),
                        })
                      }
                    >
                      <i className="fa fa-edit"></i>
                    </a>
                  </span>
                )}
                <FormSelect withoutGroup control={form.controls.shippingAddress} items={addressItems} />
              </Col>
            </Row>
            <Row>
              <Col lg="6">
                <FormInput control={form.controls.customerPoNo} />
              </Col>
              <Col lg="3">
                <FormSelect control={form.controls.boardId} items={tmBoardItems} />
              </Col>{' '}
              <Col lg="3">
                <div className="form-group ">
                  <label>
                    Salesperson
                    <span className="text-danger font-size-14 ml-1" style={{ marginTop: -5 }}>
                      *
                    </span>
                  </label>
                  <div>
                    <FormUserAvatar tooltipContainer={'layout-wrapper'} control={form.controls.salesReps} users={users} />
                  </div>
                </div>
              </Col>
            </Row>
            <Row></Row>
            {formValue?.type === 'invoice' && (
              <Row>
                <Col>
                  <FormDateInput control={form.controls.shippedDate} />
                </Col>
                <Col>
                  <ShippingMethodControl control={form.controls.shippingMethod} />
                </Col>
                <Col>
                  <FormInput control={form.controls.trackId} />
                </Col>
              </Row>
            )}

            <hr />
            <Row>
              <Col lg="6">
                <FormInput control={form.controls.memo} type="textarea" />
              </Col>
              <Col lg="6">
                <FormInput control={form.controls.message} type="textarea" />
              </Col>
            </Row>
            <hr />
            <Row style={{ flexDirection: 'row-reverse' }}>
              <Button
                onClick={() =>
                  openTMSectionModal({
                    initialValue: formValue.sections,
                    onDone: sections => {
                      form.patchValue({ sections });
                      const sectionIds = sections.map(s => R.toNumber(s.id));
                      const filteredProducts = formValue.lineItems.filter(p => sectionIds.includes(p.sectionId as number));
                      form.patchValue({ lineItems: filteredProducts });
                    },
                  })
                }
              >
                Manage Rooms
              </Button>
              <Button
                extraClassNames="mr-2"
                onClick={() =>
                  openAddProductOverlay({
                    sections: formValue.sections,
                    onDone: onProductSelection,
                    isTaxExempt: organization?.isTaxExempt,
                    defaultTierId: defaultTierId || organization?.tierId,
                    activeProducts: true,
                  })
                }
              >
                Add Product
              </Button>
            </Row>
            {(formValue.sections || []).map(section => {
              return (
                <div key={section.id} className="mb-2 mt-2">
                  <CardTitle>{section.title}</CardTitle>
                  <TimeMaterialLineStep
                    sectionId={section.id}
                    defaultTierId={defaultTierId || organization?.tierId}
                    bloc={bloc}
                    form={form}
                    users={users}
                    products={products}
                    packages={packages}
                    services={services}
                    labourTypes={labourTypes}
                    vendors={vendors}
                    classes={classes}
                    isTaxExempt={organization?.isTaxExempt}
                  />
                </div>
              );
            })}
            {/* <TimeMaterialLineStep
              defaultTierId={organization?.tierId}
              bloc={bloc}
              form={form}
              users={users}
              products={products}
              services={services}
              labourTypes={labourTypes}
              vendors={vendors}
              classes={classes}
              isTaxExempt={organization?.isTaxExempt}
            /> */}
            <div className="">
              <div className="d-flex">
                <div className="d-flex justify-content-start flex-column mb-2">
                  <a className="text-primary " onClick={() => setSetting(value => !value)}>
                    Advanced Settings
                  </a>
                  <Collapse isOpen={isSetting} className="mt-2">
                    <div className="d-flex mt-2">
                      <FormCheckbox control={form.controls.isHide} />
                    </div>
                  </Collapse>
                </div>
              </div>
              <div className="d-flex justify-content-end mb2" style={{ flex: 1 }}>
                <ProposalDiscount form={form.controls.discount} />
              </div>

              {!order && <TimeMaterialFeeLine form={form} ProposalItemsTotal={subTotal} />}
              {isSalesTaxEnabled && (
                <div className="d-flex justify-content-end mb-2">
                  <InvoiceSalesTax
                    items={salesTax}
                    isNew={!order}
                    form={form.controls.salesTax}
                    addressId={formValue?.shippingAddress}
                    lines={flatAllLineItemsValues}
                    // lines={R.map(form.controls.lineItems.items, l => ({
                    //   title: l.controls.description.value,
                    //   qty: l.controls.quantity.value,
                    //   rate: l.controls.rate.value,
                    //   taxable: l.controls.taxable.value,
                    // }))}
                  />
                </div>
              )}
            </div>
            <div style={{ flex: 1 }} className="d-flex justify-content-end">
              <div className="d-flex flex-column align-items-end">
                {organization?.isTaxExempt && (
                  <div className="mb-2">
                    <InfoBanner intent="info">{organization.name} is tax exempted</InfoBanner>
                  </div>
                )}
                {canadaFirstErrors.length > 0 && (
                  <div className="text-danger mb-2">
                    There {canadaFirstErrors.length === 1 ? 'is' : 'are'} {canadaFirstErrors.length} sales tax error
                    {canadaFirstErrors.length > 1 ? 's' : ''} that need{canadaFirstErrors.length === 1 ? 's' : ''} to be fixed.{' '}
                    <a
                      onClick={() =>
                        openViewHTMLModal({ title: 'Errors', content: `<ul>${canadaFirstErrors.map(e => `<li>${e}</li>`).join('')}</ul>` })
                      }
                    >
                      <i className="mdi mdi-eye font-size-16 text-dark"></i>
                    </a>
                  </div>
                )}
                <div>
                  <Button
                    style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    loading={submitting}
                    onClick={() => {
                      // Check Canada First errors before submitting
                      if (canadaFirstErrors.length > 0) {
                        return; // Don't submit if there are Canada First errors
                      }
                      form.submit();
                    }}
                  >
                    {order?.id ? 'Update Order' : 'Create Order'}
                  </Button>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <CardTitle className="mb-4">{OrderService.isNameEnabled() ? formValue?.name || 'Order' : <span>Order</span>}</CardTitle>
            {organization && (
              <TimeMaterialPreview
                sections={formValue.sections || []}
                terms={terms}
                form={form}
                organization={organization}
                users={users}
                labourTypes={labourTypes}
                addFee={!order}
                isEdit={isEdit}
              />
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
});
